import React, { useState, useEffect, useRef } from 'react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { FileCheck, Award, Globe, Zap } from 'lucide-react';

const stats = [
  { icon: FileCheck, value: 10000, suffix: '+', label: 'Trademarks Filed', color: 'text-[#14B8A6]', bg: 'bg-[#14B8A6]/10' },
  { icon: Award, value: 99, suffix: '%', label: 'Approval Rate', color: 'text-[#7C3AED]', bg: 'bg-[#7C3AED]/10' },
  { icon: Globe, value: 50, suffix: '', label: 'States Protected', color: 'text-[#14B8A6]', bg: 'bg-[#14B8A6]/10' },
  { icon: Zap, value: 48, suffix: 'hr', label: 'Average Filing Speed', color: 'text-[#7C3AED]', bg: 'bg-[#7C3AED]/10' },
];

const Counter = ({ target, suffix }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        const duration = 2000;
        const step = Math.max(1, Math.floor(target / (duration / 16)));
        let current = 0;
        const timer = setInterval(() => {
          current += step;
          if (current >= target) { setCount(target); clearInterval(timer); }
          else setCount(current);
        }, 16);
      }
    }, { threshold: 0.3 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target]);

  return (
    <span ref={ref}>
      {target >= 10000 ? `${Math.floor(count / 1000)}K` : count.toLocaleString()}{suffix}
    </span>
  );
};

const StatsSection = () => {
  const section = useScrollAnimation({ threshold: 0.15 });

  return (
    <section data-testid="stats-section" ref={section.ref} className="relative py-20 md:py-24 overflow-hidden" style={{
      background: 'linear-gradient(135deg, #1a0a2e 0%, #2d1052 50%, #1a0a2e 100%)'
    }}>
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-1/4 w-[300px] h-[300px] rounded-full bg-[#7C3AED]/15 blur-[100px]" />
        <div className="absolute bottom-0 right-1/4 w-[250px] h-[250px] rounded-full bg-[#14B8A6]/10 blur-[80px]" />
      </div>

      <div className="mx-auto w-full max-w-7xl px-4 md:px-8 relative z-10">
        <div className={`grid grid-cols-2 lg:grid-cols-4 gap-6 scroll-fade-up ${section.isVisible ? 'scroll-visible' : ''}`}>
          {stats.map((stat, i) => {
            const Icon = stat.icon;
            return (
              <div key={i} data-testid={`stat-card-${i}`} className="text-center group">
                <div className={`w-14 h-14 rounded-2xl ${stat.bg} flex items-center justify-center mx-auto mb-4`}>
                  <Icon className={`w-7 h-7 ${stat.color}`} />
                </div>
                <div className={`text-4xl md:text-5xl font-black ${stat.color} mb-2`} style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  <Counter target={stat.value} suffix={stat.suffix} />
                </div>
                <div className="text-sm text-white/40 font-medium">{stat.label}</div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default StatsSection;
